import { Box, Card, CardContent, Typography, Link, Stack, Skeleton, Avatar } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getCustomerPastTickets } from "../../api";
import React, { useEffect } from "react";

export const PastTickets = ({ subDomain, ticketingSystem, ticketDetails, orgId, prismToken, userEmail }) => {
    const pastTickets = useSelector((state) => state.summary.pastTickets);
    const pastTicketLoading = useSelector((state) => state.summary.pastTicketLoading);
    const dispatch = useDispatch();
    const instanceUrl = ticketDetails?.instance_ticket_url ?? "";

    useEffect(() => {
        dispatch(getCustomerPastTickets(subDomain, ticketingSystem, ticketDetails, userEmail, orgId, prismToken));
    }, [subDomain, ticketingSystem, ticketDetails, userEmail, orgId, prismToken, dispatch]);

    const getAvatarDateParts = (rawDateStr) => {
        const [year, month, day] = rawDateStr.split("T")[0]?.split("-") || [];
        const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
        return { full: rawDateStr, avatarTop: day, avatarBottom: months[parseInt(month, 10) - 1] };
    };

    const TicketCard = ({ ticket, isLast }) => {
        const { full, avatarTop, avatarBottom } = getAvatarDateParts(ticket?.created_datetime || "");

        return (
            <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ position: "relative" }}>
                <Box sx={{ position: "absolute", left: 37, top: 60, bottom: isLast ? "auto" : 0, width: "2px", bgcolor: "divider", zIndex: 0 }} />
                <Avatar sx={{ bgcolor: "#1976d2", flexDirection: "column", width: 50, height: 50, fontSize: 12, zIndex: 1, gap: "5px" }} >
                    <Box component="span" fontWeight="bold">{avatarTop}</Box>
                    <Box component="span" fontSize={10}>{avatarBottom}</Box>
                </Avatar>
                <Card variant="outlined" sx={{ flex: 1, borderRadius: 3, boxShadow: 1 }}>
                    <CardContent>
                        <Stack spacing={1}>
                            <Typography variant="caption" color="text.secondary">{full}</Typography>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {instanceUrl ? (
                                    <Link href={instanceUrl.replace("ticket_id", ticket?.id)} target="_blank" rel="noopener noreferrer" underline="hover" >
                                        #{ticket?.id}
                                    </Link>
                                ) : (
                                    <strong>#{ticket?.id}</strong>
                                )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Subject:</strong> {ticket?.subject ?? "Not provided"}
                            </Typography>
                            {ticket?.csat !== "N/A" && (
                                <Typography variant="body2" color="text.secondary">CSAT: {ticket?.csat}</Typography>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>
        );
    };

    const TicketSkeleton = () => (
        <Stack direction="row" spacing={2} alignItems="flex-start">
            <Skeleton variant="circular" width={50} height={50} />
            <Card variant="outlined" sx={{ flex: 1, borderRadius: 3 }}>
                <CardContent>
                    <Skeleton width="30%" />
                    <Skeleton width="60%" />
                    <Skeleton width="90%" />
                </CardContent>
            </Card>
        </Stack>
    );

    return (
        <Box sx={{ width: "100%", height: '100%', overflowY: 'auto', py: 3 }}>
            <Stack spacing={4} sx={{ width: "95%", margin: "0 auto" }}>
                {pastTicketLoading ? (
                    Array.from({ length: 5 }).map((_, index) => <TicketSkeleton key={index} />)
                ) : pastTickets.length > 0 ? (
                    pastTickets.map((ticket, index) => (
                        <TicketCard
                            key={ticket.id}
                            ticket={ticket}
                            isLast={index === pastTickets.length - 1}
                        />
                    ))
                ) : (
                    <Box textAlign="center" py={6}>
                        <Typography variant="h6" color="text.secondary">
                            No past tickets found.
                        </Typography>
                    </Box>
                )}
            </Stack>
        </Box>
    );
};
